import { Reveal } from "../lib/motion";
import { FileText, BookOpen, Brain, Infinity as InfinityIcon, ClipboardCheck, ArrowUpRight } from "lucide-react";

const DOCS = [
  {
    icon: FileText,
    tag: "PRODUCT DOSSIER",
    title: "NexSys™ Product Dossier",
    desc: "An overview of the platform, its architecture, capabilities, and enterprise applications.",
    href: "https://customer-assets-lqy194kg.emergentagent.net/job_nexis-future/artifacts/f453e083f077186d_NexSys_Dossier_v2%202.pdf",
  },
  {
    icon: BookOpen,
    tag: "FAQ",
    title: "NexSys™ — Explained Simply",
    desc: "An accessible introduction to NexSys, its purpose, and how it differs from conventional enterprise AI.",
    href: "https://customer-assets-lqy194kg.emergentagent.net/job_nexis-future/artifacts/a90ae9d06f340070_NexSys_FAQ_v2.pdf",
  },
  {
    icon: Brain,
    tag: "FOUNDATIONAL RESEARCH",
    title: "SCIOUS™ — Computational Awareness Paradigm",
    desc: "The foundational research establishing our state-centric approach to computational intelligence.",
    href: "https://customer-assets-lqy194kg.emergentagent.net/job_nexis-future/artifacts/f4e1d2184c02ebd5_1787699685712%20%281%29.pdf",
  },
  {
    icon: InfinityIcon,
    tag: "ARCHITECTURAL RESEARCH",
    title: "NexSys™ Continuum",
    desc: "Architectural research describing governed state persistence and continuity across interactions.",
    href: "https://customer-assets-lqy194kg.emergentagent.net/job_nexis-future/artifacts/e5103871d89dbad5_NexSys_Continuum_Whitepaper_Section%283%29.pdf",
  },
  {
    icon: ClipboardCheck,
    tag: "FIELD EVIDENCE · ONGOING VALIDATION",
    title: "NexSys™ — Governed Intelligence in Production",
    desc: "Our October 6, 2026 field evidence summary documenting specific observed production behaviors and ongoing validation.",
    href: null,
  },
];

export default function Research() {
  return (
    <section
      id="research"
      className="relative border-t border-white/5 bg-nexis-bg/85 py-24 backdrop-blur-sm md:py-32"
      data-testid="research-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal>
          <span className="font-mono text-xs tracking-[0.25em] text-nexis-purple">
            — NEXIS | INNOVATION, RESEARCH &amp; DEVELOPMENT
          </span>
          <h2 className="mt-6 max-w-3xl font-display text-3xl font-light leading-tight tracking-tight lg:text-5xl" data-testid="research-headline">
            Exploring the evolution of <span className="text-gradient">governed computational intelligence</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/55">
            NEXIS is a boutique technology and innovation firm advancing enterprise intelligence through
            research, engineering, and practical innovation. Our proprietary NexSys™ Computational
            Intelligence Platform represents an important part of that journey. The following materials
            provide insight into our architectural foundations, technological development, demonstrated
            capabilities, and ongoing research.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {DOCS.map((d, i) => {
            const Inner = (
              <>
                <div className="flex items-start justify-between">
                  <d.icon className="h-5 w-5 text-nexis-blue" />
                  {d.href ? (
                    <ArrowUpRight className="h-4 w-4 text-white/30 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-nexis-blue" />
                  ) : null}
                </div>
                <span className="mt-5 font-mono text-[10px] tracking-[0.22em] text-white/35">{d.tag}</span>
                <h3 className="mt-2 font-display text-base font-medium text-white">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/50">{d.desc}</p>
                <span className="mt-4 font-mono text-[11px] tracking-widest text-white/40">
                  {d.href ? "VIEW PDF →" : "DOCUMENT AVAILABLE ON REQUEST"}
                </span>
              </>
            );
            return (
              <Reveal key={d.title} delay={i * 0.06}>
                {d.href ? (
                  <a
                    href={d.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group glass glass-hover flex h-full flex-col rounded-2xl p-6"
                    data-testid={`research-doc-${i}`}
                  >
                    {Inner}
                  </a>
                ) : (
                  <div className="glass flex h-full flex-col rounded-2xl p-6 opacity-80" data-testid={`research-doc-${i}`}>
                    {Inner}
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
